import React, { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import { formatDate } from '../utils/dateFormatting';
import { Table, Badge } from './ui';

const AllJobsTable = ({
  projects,
  contacts = [],
  onRowClick,
  onProjectUpdate,
  visibleColumns: externalVisibleColumns,
  autoResizeEnabled = true,
}) => {
  // Use external visibility state if provided, otherwise use local state
  const [localVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('all-jobs-visible-columns');
    return saved ? JSON.parse(saved) : {
      action: true,
      jobNumber: true,
      projectName: true,
      clientName: true,
      contactName: true,
      agreementStatus: true,
      billingStatus: true,
      readyToInvoice: false,
      modelPrice: true,
      calculatedPrice: true,
      poNumber: true,
      initialAcceptance: true,
      invoicedDate: true,
      paidDate: true,
      address: false,
      modelingStatus: false
    };
  });

  const visibleColumns = externalVisibleColumns || localVisibleColumns;
  const [localAutoResizeEnabled, setLocalAutoResizeEnabled] = useState(true);
  const currentAutoResizeEnabled = externalVisibleColumns ? autoResizeEnabled : localAutoResizeEnabled;

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (currentAutoResizeEnabled && !externalVisibleColumns) {
      setLocalAutoResizeEnabled(false);
    }
  };

  // Format price
  const formatPrice = (price) => {
    if (!price && price !== 0) return '—';
    return `$${Number(price).toLocaleString()}`;
  };

  // Format job number with revision
  const formatJobNumber = (project) => {
    const baseNumber = project.jobNumber || '—';
    const revision = project.jobRevNumber;
    return revision && revision !== '0' ? `${baseNumber} Rev ${revision}` : baseNumber;
  };

  // Get status badge variant
  const getStatusVariant = (status, type) => {
    if (!status) return 'neutral';

    if (type === 'billing') {
      if (['Paid', 'Invoiced'].includes(status)) return 'success';
      if (['Quoted'].includes(status)) return 'warning';
      if (['Voided', 'Not Paid'].includes(status)) return 'danger';
    }

    if (type === 'ready') {
      if (status === 'Yes') return 'success';
    }

    if (type === 'agreement') {
      if (['Ready', 'Utility Ready', 'Completed'].includes(status)) return 'success';
      if (['Sent'].includes(status)) return 'info';
      if (['Pending', 'Hold'].includes(status)) return 'warning';
      if (['Not Ready', 'Not Signed', 'Voided'].includes(status)) return 'danger';
    }

    if (type === 'modeling') {
      if (status === 'Completed') return 'success';
      if (['Modeling', 'Review', 'Double Check'].includes(status)) return 'info';
      if (['Startup', 'Data Prep'].includes(status)) return 'warning';
    }

    return 'neutral';
  };

  // Define all columns for the Table component
  const allColumns = useMemo(() => [
    {
      id: 'action',
      label: '',
      field: 'id',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRowClick(row);
          }}
          style={{
            padding: '4px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'background-color 0.2s, color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
            e.currentTarget.style.color = 'var(--cta-blue)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
          }}
          title="View Details"
        >
          <Info size={16} />
        </button>
      ),
    },
    {
      id: 'jobNumber',
      label: 'Job Number',
      field: 'jobNumber',
      width: 120,
      minWidth: 90,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value, row) => (
        <span style={{ fontWeight: 'var(--font-medium)', color: 'var(--gray-200)' }}>
          {formatJobNumber(row)}
        </span>
      ),
    },
    {
      id: 'projectName',
      label: 'Project Name',
      field: 'projectName',
      width: 220,
      minWidth: 140,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'clientName',
      label: 'Client',
      field: 'clientName',
      width: 160,
      minWidth: 110,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'contactName',
      label: 'Contact',
      field: 'contactName',
      width: 140,
      minWidth: 90,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'agreementStatus',
      label: 'Agreement',
      field: 'agreementStatus',
      width: 130,
      minWidth: 110,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => value ? (
        <Badge variant={getStatusVariant(value, 'agreement')} size="sm">
          {value}
        </Badge>
      ) : '—',
    },
    {
      id: 'billingStatus',
      label: 'Billing',
      field: 'billingStatus',
      width: 110,
      minWidth: 90,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => value ? (
        <Badge variant={getStatusVariant(value, 'billing')} size="sm">
          {value}
        </Badge>
      ) : '—',
    },
    {
      id: 'readyToInvoice',
      label: 'Ready',
      field: 'readyToInvoice',
      width: 90,
      minWidth: 80,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => value ? (
        <Badge variant={getStatusVariant(value, 'ready')} size="sm">
          {value}
        </Badge>
      ) : '—',
    },
    {
      id: 'modelPrice',
      label: 'Model Price',
      field: 'modelPrice',
      width: 110,
      minWidth: 90,
      sortable: true,
      resizable: true,
      contentType: 'number',
      align: 'right',
      render: (value, row) => formatPrice(value || row.price),
    },
    {
      id: 'calculatedPrice',
      label: 'Calc. Price',
      field: 'calculatedModelPrice',
      width: 110,
      minWidth: 90,
      sortable: true,
      resizable: true,
      contentType: 'number',
      align: 'right',
      render: (value) => (
        <span style={{
          color: value ? 'var(--success)' : 'var(--gray-500)',
          fontWeight: value ? 'var(--font-medium)' : 'normal'
        }}>
          {formatPrice(value)}
        </span>
      ),
    },
    {
      id: 'poNumber',
      label: 'PO #',
      field: 'poNumber',
      width: 100,
      minWidth: 80,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'initialAcceptance',
      label: 'Acceptance',
      field: 'initialAcceptance',
      width: 110,
      minWidth: 95,
      sortable: true,
      resizable: true,
      contentType: 'date',
      render: (value) => value ? formatDate(value) : '—',
    },
    {
      id: 'invoicedDate',
      label: 'Invoiced',
      field: 'invoicedDate',
      width: 105,
      minWidth: 95,
      sortable: true,
      resizable: true,
      contentType: 'date',
      render: (value) => value ? formatDate(value) : '—',
    },
    {
      id: 'paidDate',
      label: 'Paid',
      field: 'paidDate',
      width: 105,
      minWidth: 95,
      sortable: true,
      resizable: true,
      contentType: 'date',
      render: (value) => value ? formatDate(value) : '—',
    },
    {
      id: 'address',
      label: 'Address',
      field: 'address',
      width: 200,
      minWidth: 130,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || '—'}
        </div>
      ),
    },
    {
      id: 'modelingStatus',
      label: 'Modeling',
      field: 'modelingStatus',
      width: 120,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => value ? (
        <Badge variant={getStatusVariant(value, 'modeling')} size="sm">
          {value}
        </Badge>
      ) : '—',
    },
  ], [onRowClick]);

  // Get visible columns for table display
  const columns = useMemo(() => {
    return allColumns.filter(col => visibleColumns[col.id]);
  }, [allColumns, visibleColumns]);

  return (
    <Table
      data={projects}
      columns={columns}
      sortable={true}
      searchable={false}
      resizable={true}
      paginated={false}
      storageKey="all-jobs-table"
      autoResizeOnDataChange={currentAutoResizeEnabled}
      showAutoSizeButton={false}
      disableResizeListener={true}
      allowNaturalWidth={true}
      onManualResize={handleManualResize}
      emptyMessage="No projects found"
      onRowClick={onRowClick}
      variant="dense"
      stickyHeader={true}
      style={{ border: 'none', borderRadius: '0', width: '100%', overflow: 'visible' }}
    />
  );
};

export default AllJobsTable;
